import React from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ChevronDownIcon } from '@motorway/mw-highway-code';

import LocalTexts from './HeaderTexts.json';
import { NavRoute, SubRoutesArray, SubRouteType } from './useHeaderNav';

import styles from './Header.scss';

const LocalT = new MDText(LocalTexts);
const t = (key: string) => LocalT.translate(key) as string;

export type NavVariant = 'desktop' | 'mobile';

type TrackingHandlers = {
  fireMenuHoverTracking: (message: string, name?: string) => void;
  fireTrackingEventForLink: (path: string, name: string) => void;
  isExternalLink: (route: string) => boolean;
};

type NavItemProps = TrackingHandlers & {
  activeGroup: string | null;
  route: NavRoute;
  setActiveGroup: (group: string | null) => void;
  variant: NavVariant;
};

const renderSubRouteContent = ({ caption, icon, label }: SubRouteType) => (
  <>
    { icon && <div className={cx(styles.icon, styles[icon])} /> }
    <div>
      <span className={styles.label}>
        { t(label) }
      </span>
      { caption && <span className={styles.caption}>{t(caption)}</span> }
    </div>
  </>
);

const renderSubRoutes = (subRoutes: SubRoutesArray, { fireTrackingEventForLink, isExternalLink }: TrackingHandlers) =>
  subRoutes.map(({
    caption,
    icon,
    label,
    path,
    trackingPath,
  }: SubRouteType) => (
    <li
      key={`navlink-sub-${label}`}
      className={cx(styles.navItem, {
        [styles.withCaption]: caption,
      })}
    >
      {isExternalLink(path) ? (
        <a
          href={path}
          onClick={() => fireTrackingEventForLink(trackingPath || path, label)}
          rel="noopener noreferrer"
          target="_blank"
        >
          { renderSubRouteContent({ caption, icon, label, path }) }
        </a>
      ) : (
        <Link
          onClick={() => fireTrackingEventForLink(trackingPath || path, label)}
          to={path}
        >
          { renderSubRouteContent({ caption, icon, label, path }) }
        </Link>
      )}
    </li>
  ));

type LinkTrackingHandlers = Pick<TrackingHandlers, 'fireTrackingEventForLink' | 'isExternalLink'>;

type TopLevelLinkProps = LinkTrackingHandlers & { route: NavRoute };

const TopLevelLink = ({ fireTrackingEventForLink, isExternalLink, route }: TopLevelLinkProps) => {
  const { label, path, trackingPath } = route;

  if (isExternalLink(path)) {
    return (
      <a
        href={path}
        onClick={() => fireTrackingEventForLink(trackingPath || path, label)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {t(label)}
      </a>);
  }
  return (
    <Link
      onClick={() => fireTrackingEventForLink(trackingPath || path, label)}
      to={path}
    >
      {t(label)}
    </Link>);
};

export const NavItem = ({
  activeGroup,
  fireMenuHoverTracking,
  fireTrackingEventForLink,
  isExternalLink,
  route,
  setActiveGroup,
  variant,
}: NavItemProps) => {
  const { label, name, path, subRoutes } = route;
  const tracking = { fireMenuHoverTracking, fireTrackingEventForLink, isExternalLink };

  if (!subRoutes) {
    return (
      <li
        key={`navlink-${path}-${label}`}
        className={cx(styles.navItem, styles.topLevel)}
        data-name={name}
      >
        <TopLevelLink {...{ fireTrackingEventForLink, isExternalLink, route }} />
      </li>
    );
  }

  const active = activeGroup === label;
  const isMobile = variant === 'mobile';

  return (
    <li
      key={`navlink-${name}-${label}`}
      className={styles.navItem}
      data-cy={isMobile ? name : undefined}
      data-name={name}
    >
      <div
        className={cx(styles.toggle, {
          [styles.active]: active,
        })}
      >
        {isMobile ? (
          <button
            aria-controls={name}
            aria-expanded={active}
            className={styles.groupToggle}
            onClick={() => setActiveGroup(active ? null : label)}
            onMouseEnter={() => fireMenuHoverTracking(label, name)}
            type='button'
          >
            { t(`${label}`) }
            <ChevronDownIcon />
          </button>
        ) : (
          <div
            className={styles.groupToggle}
            onMouseEnter={() => fireMenuHoverTracking(label, name)}
          >
            <span>{t(`${label}`)}</span>
            <ChevronDownIcon />
          </div>
        )}
        <div className={cx(styles.subMenu)} id={isMobile ? name : undefined}>
          <ul>{renderSubRoutes(subRoutes, tracking)}</ul>
        </div>
      </div>
    </li>
  );
};
